import { useState } from 'react';
import { API_AUTH_ERROR_MAP } from '@packages/shared/domain/auth/errors/AuthErrorMap';
import { type LoginRequest } from '@packages/shared/domain/auth/schemas/AuthRequestSchemas';
import { ApiErrorConverter } from '@packages/shared/domain/shared/errors/ApiErrorConverter';
import { UserRole } from '@packages/shared/domain/user/UserRole';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Routes } from '@/config/Routes';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import { LoginForm } from '@/features/auth/components/LoginForm';
import { logoutUser } from '@/features/auth/api/auth.api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useLogin } from '@/features/auth/hooks/useLogin';

const MOBILE_ALLOWED_ROLES = new Set<string>([UserRole.TECHNICIAN, UserRole.DUAL]);

export const Route = createFileRoute('/_guest/login')({
	component: LoginPage,
});

function LoginPage() {
	const { t: tErrors } = useTranslation('errors');
	const navigate = useNavigate();
	const { invalidateAuth, clearAuth } = useAuth();
	const [loginError, setLoginError] = useState<string | undefined>(undefined);

	const {
		mutate: login,
		isPending,
		isSuccess,
	} = useLogin({
		onSuccess: async (response) => {
			if (!MOBILE_ALLOWED_ROLES.has(response.user.role)) {
				await logoutUser();
				clearAuth();
				setLoginError(tErrors('mobileAccessRestricted'));
				return;
			}
			invalidateAuth();
			navigate({ to: Routes.HOME });
		},
		onError: (error) => {
			const translationKey = ApiErrorConverter.toTranslationKey(error, API_AUTH_ERROR_MAP);
			setLoginError(tErrors(translationKey));
		},
	});

	const handleLoginSubmit = async (credentials: LoginRequest) => {
		setLoginError(undefined);

		if (Capacitor.isNativePlatform()) {
			try {
				await Keyboard.hide();
			} catch {
				// Keyboard plugin may not be available in all environments
			}
		}

		login(credentials);
	};

	const isLoading = isPending || (isSuccess && loginError === undefined);

	return (
		<AuthLayout>
			<LoginForm onSubmit={handleLoginSubmit} isLoading={isLoading} error={loginError} />
		</AuthLayout>
	);
}
